import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable , throwError } from 'rxjs';
import {catchError, map, shareReplay, tap} from 'rxjs/operators';
import {RestUrlsConfig} from '@signature-it/ngx-generic/config';
import { IUser } from '@signature-it/ngx-generic/interfaces';
import { UserFactory, UserPropertyFieldDTO } from '@signature-it/ngx-generic/models';
import { CommonService } from "../services/common.service";
import {PageNotificationService} from './page-notification';

declare let _: any;

@Injectable({
    providedIn: 'root'
})
export class UserService extends CommonService {
    protected userDetailsUrl = `${this.restUrlsConf.appURL}/public/user/details.json`;
    protected userCurrDetailsUrl = `${this.restUrlsConf.appURL}/user/current/details.json`;
    protected userRegistrationUrl = `${this.restUrlsConf.appURL}/public/user/registration.json`;
    protected addressUrl = `${this.restUrlsConf.appURL}/userB2B/address.json`;
    protected addressB2CUrl = `${this.restUrlsConf.appURL}/public/userB2C/address.json`;
    protected updateAddressB2CUrl = `${this.restUrlsConf.appURL}/public/userB2C/update_address.json`;
    protected updateFirstLoginUrl = `${this.restUrlsConf.appURL}/public/user/update_first_login.json`;
    protected getProfilesUrl = `${this.restUrlsConf.appURL}/public/user/profiles.json`;

    protected usersLists: {} = {};

    constructor(protected http: HttpClient,
                protected restUrlsConf: RestUrlsConfig,
                protected notifySvc: PageNotificationService) {
        super(http, restUrlsConf, notifySvc);
    }

    /**
     * Getting current user's attributes<br>
     * User's type is detected by factory on create.<br>
     * <b>NOTE:</b> If user is in phone order mode, then returns relevant user.
     *
     * @returns Observable<IUser> if user logged in the system
     * @returns Observable<Response> if exception occurred
     */
    getUserDetails(extended?: boolean, setPhoneOrder = true): Observable<IUser> {
        const params = new HttpParams();
        params.set('extended', (extended || false).toString());
        return this.http.get<IUser>(this.userDetailsUrl, { params })
            .pipe(map((res) => {
                const data = res;
                return UserFactory.create(data);
            }), catchError((error: Response) => throwError(error)));
    }

	/**
	 * Getting current user's attributes<br>
	 * User's type is detected by factory on create.<br>
	 * <b>NOTE:</b> If user is in phone order mode, returns always current user.
	 *
	 * @returns Observable<IUser> if user logged in the system
	 * @returns Observable<Response> if exception occurred
	 */
	getCurrentUserDetails(extended?: boolean): Observable<IUser> {
		const params = new HttpParams();
		params.set('extended', (extended|| false).toString());
		return this.http.get<IUser>(this.userCurrDetailsUrl, { params })
			.pipe(map((res) => {
				const data = res;
				return UserFactory.create(data);
			}), catchError((error: Response) => throwError(error)));
	}

    changeUserActive(uid: number|string, params: object): Observable<boolean> {
        const url = `${this.restUrlsConf.appURL}/user/${uid}/change_active.json`;
        return this.http.post<boolean>(url, JSON.stringify(params))
            .pipe(map((res) => res), catchError((error: Response) => throwError(error)));
    }

    userRegistration(form: any): Observable<any> {
        return this.http.post(this.userRegistrationUrl, JSON.stringify(form))
            .pipe(map((res) => res), catchError( (error: any) => throwError(error)));
    }

    getAddress(): Observable<any> {
        return this.http.get(this.addressUrl)
            .pipe(tap((res) => res, (error: any) => throwError(error)));
    }

    getAddressB2C(): Observable<any> {
        return this.http.get(this.addressB2CUrl)
            .pipe(tap((res) => res, (error: any) => throwError(error)));
    }

    changeUserPassword( params: object): Observable<boolean> {
        const url = `${this.restUrlsConf.appURL}/public/user/change_password.json`;
        return this.http.post<boolean>(url, JSON.stringify(params))
            .pipe(map((res) => res), catchError((error: Response) => throwError(error)));
    }

    updateProfileDetails( params: object): Observable<boolean> {
        const url = `${this.restUrlsConf.appURL}/public/user/update_profile_details.json`;
        return this.http.post<boolean>(url, JSON.stringify(params))
            .pipe(map((res) => res), catchError((error: Response) => throwError(error)));
    }

    getMoreDetails(uid: number|string): Observable<any> {
        const url = `${this.restUrlsConf.appURL}/public/user/get_more_details.json`;
        return this.http.post<any>(url, JSON.stringify(uid))
            .pipe(map((res) => {
                return {
                    userData: res['userData'],
                    moreDetails: UserPropertyFieldDTO.createFromArray(res['moreDetails']),
                    personalDetails: UserPropertyFieldDTO.createFromArray(res['personalDetails']),
                }
            }), catchError((error: Response) => throwError(error)));
    }


    updateAddress( params: object): Observable<boolean> {
        const url = `${this.restUrlsConf.appURL}/public/userB2B/update_address.json`;
        return this.http.post<boolean>(url, JSON.stringify(params))
            .pipe(map((res) => res), catchError((error: Response) => throwError(error)));
    }

    updateAddressB2C ( params: object): Observable<boolean> {
        return this.http.post<boolean>(this.updateAddressB2CUrl, JSON.stringify(params))
            .pipe(map((res) => res), catchError((error: Response) => throwError(error)));
    }

    updateFirstLogin() {
        return this.http.get(this.updateFirstLoginUrl)
            .pipe(map((res) => res), catchError((error: Response) => throwError(error)));
    }

    getProfiles() {
        return this.http.get(this.getProfilesUrl)
            .pipe(map((res) => res), catchError((error: Response) => throwError(error)));
    }

    getNameById(uid: number): Observable<any> {
        const url = `${this.restUrlsConf.appURL}/admin/user/get_name_by_id`;
        return this.http.post<string>(url, JSON.stringify(uid))
            .pipe(map((res) => res), catchError((error: Response) => throwError(error)));
    }


    getUsersAutocomplete(value) {
        return this.http.post(`${this.restUrlsConf.appURL}/admin/users-list/autocomplete`, JSON.stringify({term: value}));
    }

    getUsersListByGroupId(groupIds): Observable<any> {
        if (!this.usersLists[groupIds]) {
            this.usersLists[groupIds] = this.http.get(`${this.restUrlsConf.appURL}/admin/users-list/${groupIds}`).pipe(
                shareReplay(1)
            );
        }
        return this.usersLists[groupIds];
    }
}
